
import React from "react";
import { i18nManager } from "../../i18n";
import loader from "../../assets/images/loading_circle.gif";
import { alertDialog, AlertInterface, Button, Scheme } from "@rivtn/noseur";

interface PageProps {
    content?: string;
    defaultInput?: string;
    inputType: "AMOUNT" | "PLAIN";
    action?: (type: "SUCCESS" | "FAILURE", inputOne?: string, inputTwo?: string) => Promise<string | void>;
}

export interface SimulateProps {
    currency?: string;
    pages: {
        one: PageProps;
        two?: PageProps;
    };
}

function Simulate(props: SimulateProps) {

    const labels = i18nManager.Labels.simulator;
    let inputOne = props.pages?.one?.defaultInput;
    let inputTwo = props.pages?.two?.defaultInput;
    const pagesSize = Object.keys(props.pages!).length;
    const errorRef = React.useRef<HTMLSpanElement>(null);
    const inputRef = React.useRef<HTMLSpanElement>(null);
    const loaderRef = React.useRef<HTMLDivElement>(null);

    if (process.env.REACT_APP_ENV === "production") {
        return undefined;
    }

    console.log("--------", inputOne, inputTwo);
    return (<>
        <span className="simulate" onClick={() => showSimulationDevice(props.pages?.one!)}>{i18nManager.Labels.common.simulate}</span>
    </>);

    function showSimulationDevice(page: PageProps, isSecondPage?: boolean) {
        const dialog = alertDialog({
            cancel: null,
            confirm: null,
            componentProps: {
                contentProps: {
                    style: {
                        borderRadius: 30,
                        background: "#303030",
                    }
                }
            },
            style: {
                height: "unset"
            },
            message: (<div className="simulator">
                <div className="container-loading" ref={loaderRef} style={{ display: "none" }}>
                    <img src={loader} alt={i18nManager.Labels.loading.loader_alt} />
                </div>
                <div className="attrs">
                    <span className="time">19:30</span>
                    <div className="notch" />
                    <div className="wifi-battery">
                        <i className="fa fa-wifi" />
                        <i className="fa fa-battery" />
                    </div>
                </div>
                <div className="controls">
                    <Button textOnly scheme={Scheme.DARK} text={isSecondPage ? labels.back : labels.close} leftIcon={isSecondPage ? "fa fa-angle-left" : "fa fa-times"}
                        onClick={() => {
                            if (isSecondPage) {
                                dialog.destroy(() => showSimulationDevice(props.pages?.one!));
                                return;
                            }
                            dialog.hide();
                        }} />
                    {page?.action && (<div>
                        <Button textOnly scheme={Scheme.SUCCESS} text={labels.success} onClick={() => report(page, dialog, "SUCCESS")} />
                        <Button textOnly scheme={Scheme.DANGER} text={labels.failure} onClick={() => report(page, dialog, "FAILURE")} />
                    </div>)}
                </div>
                <div className="content">
                    {page?.content}
                </div>
                <span className="error-text" ref={errorRef} />
                <div className="interactive">
                    <span className="input">{page?.inputType === "AMOUNT" ? props.currency : " "} <span ref={inputRef}>{page?.defaultInput}</span></span>
                    <div className="trackpad">
                        <div className="row">
                            <span onClick={() => onKeyPress("1", isSecondPage)}>1</span>
                            <span onClick={() => onKeyPress("2", isSecondPage)}>2 <span>ABC</span></span>
                            <span onClick={() => onKeyPress("3", isSecondPage)}>3 <span>DEF</span></span>
                            <span onClick={() => onKeyPress("#", isSecondPage)}>#</span>
                        </div>
                        <div className="row">
                            <span onClick={() => onKeyPress("4", isSecondPage)}>4 <span>GHI</span></span>
                            <span onClick={() => onKeyPress("5", isSecondPage)}>5 <span>JKL</span></span>
                            <span onClick={() => onKeyPress("6", isSecondPage)}>6 <span>MNO</span></span>
                            <span onClick={() => onKeyPress("+", isSecondPage)}>+</span>
                        </div>
                        <div className="row">
                            <span onClick={() => onKeyPress("7", isSecondPage)}>7 <span>PQRS</span></span>
                            <span onClick={() => onKeyPress("8", isSecondPage)}>8 <span>TUV</span></span>
                            <span onClick={() => onKeyPress("9", isSecondPage)}>9 <span>WXYZ</span></span>
                            <span onClick={() => onKeyPress("BACK", isSecondPage)}>⌫</span>
                        </div>
                        <div className="row">
                            <span onClick={() => onKeyPress("*", isSecondPage)}>* <span>#</span></span>
                            <span onClick={() => onKeyPress("0", isSecondPage)}>0 <span>+</span></span>
                            <span onClick={() => onKeyPress(".", isSecondPage)}>.</span>
                            <span onClick={() => confirm(page, dialog)}><i className="fa fa-check" /></span>
                        </div>
                    </div>
                    <div className="slider" />
                </div>
            </div>),
        });
        dialog.show();
    }

    async function onKeyPress(key: string, isSecondPage?: boolean) {
        if (!inputRef.current) return;
        if (errorRef.current && errorRef.current.innerText) errorRef.current.innerText = "";
        const inputValue = inputRef.current.innerText;
        switch (key) {
            case "BACK":
                inputRef.current.innerText = inputValue.substring(0, inputValue.length - 1);
                break;
            default:
                inputRef.current.innerText += key;
        }
        if (isSecondPage) {
            inputTwo = inputRef.current.innerText;
            props.pages!.two!.defaultInput = inputTwo;
            return;
        }
        inputOne = inputRef.current.innerText;
        props.pages!.one!.defaultInput = inputOne;
    }

    async function confirm(page: PageProps, dialog: AlertInterface) {
        const isPageOne = page === props.pages?.one;
        if (isPageOne) {
            if (pagesSize === 1) {
                report(page, dialog);
            } else {
                dialog.destroy(() => showSimulationDevice(props.pages?.two!, true));
            }
        } else {
            report(page, dialog);
        }
    }

    async function report(page: PageProps, dialog: AlertInterface, type: "SUCCESS" | "FAILURE" = "SUCCESS") {
        if (errorRef.current && errorRef.current.innerText) errorRef.current.innerText = "";
        if (!page?.action) {
            dialog.hide();
            return;
        }
        if (!inputOne) inputOne = "0";
        loaderRef.current!.style.display = "flex";
        await page.action!(type, `${parseInt(inputOne) * 1000000}`, (inputTwo ? `${parseInt(inputTwo) * 1000000}` : undefined)).then((result) => {
            if (result?.length) {
                throw new Error(result);
            }
            dialog.hide();
            return;
        }).catch((error) => {
            if (errorRef.current) errorRef.current.innerText = `${error}`;
        }).finally(() => {
            if (loaderRef.current) loaderRef.current!.style.display = "none";
        });
    }

}

export default Simulate;
